/**
 * Native JavaScript alternatives to lodash functions
 * These utilities provide the same functionality without the lodash dependency
 */

/**
 * Checks if a path array is empty or invalid
 */
function isEmptyPath(pathArray: string[]): boolean {
  return (
    pathArray.length === 0 || (pathArray.length === 1 && pathArray[0] === '')
  );
}

/**
 * Checks if a value can be traversed (is an object)
 */
function isTraversable(value: unknown): boolean {
  return value !== null && value !== undefined && typeof value === 'object';
}

/**
 * Gets the value at path of object. If the resolved value is undefined,
 * the defaultValue is returned in its place.
 *
 * @param object - The object to query
 * @param path - The path of the property to get (string or array)
 * @param defaultValue - The value returned for undefined resolved values
 * @returns The resolved value
 */
export function get<T = unknown>(
  object: unknown,
  path: string | string[],
  defaultValue?: T,
): T {
  if (!object || typeof object !== 'object') {
    return defaultValue;
  }

  const pathArray = Array.isArray(path) ? path : path.split('.');

  if (isEmptyPath(pathArray)) {
    return defaultValue;
  }

  let result: unknown = object;

  for (const key of pathArray) {
    if (!isTraversable(result)) {
      return defaultValue;
    }
    result = (result as Record<string, unknown>)[key];
  }

  return result === undefined ? defaultValue : (result as T);
}

/**
 * Checks if value is an empty object, collection, map, or set.
 *
 * @param value - The value to check
 * @returns True if value is empty, else false
 */
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }

  if (Array.isArray(value) || typeof value === 'string') {
    return value.length === 0;
  }

  if (value instanceof Map || value instanceof Set) {
    return value.size === 0;
  }

  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }

  return false;
}

/**
 * Compares two arrays for deep equality
 */
function compareArrays(a: unknown[], b: unknown[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((item, index) => isEqual(item, b[index]));
}

/**
 * Compares two objects for deep equality
 */
function compareObjects(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
): boolean {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) {
    return false;
  }

  return keysA.every(key => keysB.includes(key) && isEqual(a[key], b[key]));
}

const compareMaybeArrays = (a: unknown, b: unknown): boolean | undefined => {
  const aIsArray = Array.isArray(a);
  const bIsArray = Array.isArray(b);
  if (!aIsArray && !bIsArray) {
    // Neither is an array, return undefined
    return undefined;
  }
  if (aIsArray && bIsArray) {
    // Both are arrays, we can compare them
    return compareArrays(a as unknown[], b as unknown[]);
  }
  // One is array, other is not, return false
  return false;
};

/**
 * Performs a deep comparison between two values to determine if they are equivalent.
 *
 * @param a - The value to compare
 * @param b - The other value to compare
 * @returns True if the values are equivalent, else false
 */
export function isEqual(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }

  if (a === null || a === undefined || b === null || b === undefined) {
    return a === b;
  }

  const arraysAreEqual = compareMaybeArrays(a, b);
  if (arraysAreEqual !== undefined) {
    return arraysAreEqual;
  }

  if (typeof a === 'object' && typeof b === 'object') {
    return compareObjects(
      a as Record<string, unknown>,
      b as Record<string, unknown>,
    );
  }

  return false;
}

/**
 * Creates a deep clone of value.
 *
 * @param value - The value to clone
 * @returns The cloned value
 */
export function cloneDeep<T>(value: T): T {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(item => cloneDeep(item)) as unknown as T;
  }

  if (value instanceof Date) {
    return new Date(value.getTime()) as unknown as T;
  }

  if (value instanceof RegExp) {
    return new RegExp(value) as unknown as T;
  }

  const cloned = {} as Record<string, unknown>;
  for (const key in value) {
    if (Object.prototype.hasOwnProperty.call(value, key)) {
      cloned[key] = cloneDeep((value as Record<string, unknown>)[key]);
    }
  }

  return cloned as T;
}

/**
 * Creates an array of numbers progressing from 0 up to, but not including n.
 *
 * @param n - The number of times to repeat
 * @returns An array of numbers from 0 to n-1
 */
export function times(n: number): number[] {
  return Array.from({ length: n }, (_, i) => i);
}

/**
 * A no-operation function that returns undefined regardless of the arguments it receives.
 */
export function noop(..._: unknown[]): void {
  // Intentionally empty
}
